import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import { territoriesApi, resourcesApi, partnersApi } from '../services/api';
import InteractiveMap from '../components/InteractiveMap';
import LiveRanking from '../components/LiveRanking';
import TerritoryCard from '../components/TerritoryCard';
import heroImage from '../assets/main2.png';

// ── URL resolver ───────────────────────────────────────────────────────────────
const resolveUrl = (url, folderName = '') => {
  if (!url) return '';
  if (url.startsWith('http') || url.startsWith('data:') || url.startsWith('blob:')) return url;
  const baseUrl = process.env.REACT_APP_API_BASE || 'http://localhost:3001';
  let cleanUrl  = url.replace(/['"]/g, '').replace(/^\/+/, '');
  if (!cleanUrl.startsWith('uploads/') && folderName) {
    cleanUrl = `uploads/${folderName}/${cleanUrl}`;
  }
  return `${baseUrl}/${cleanUrl}`;
};

// ── Video URL → embed URL ──────────────────────────────────────────────────────
const getSafeVideoUrl = (url) => {
  if (!url || typeof url !== 'string') return '';
  const s = url.trim();
  try {
    if (s.includes('/embed/') || s.includes('player.vimeo.com')) return s;
    if (s.includes('youtube.com/watch')) {
      const u = new URL(s.startsWith('http') ? s : `https://${s}`);
      const v = u.searchParams.get('v');
      if (v) return `https://www.youtube.com/embed/${v}?rel=0`;
    }
    if (s.includes('youtu.be/')) {
      const id = s.split('youtu.be/')[1].split(/[?&]/)[0];
      if (id) return `https://www.youtube.com/embed/${id}?rel=0`;
    }
    if (s.includes('youtube.com/shorts/')) {
      const id = s.split('/shorts/')[1].split(/[?&]/)[0];
      if (id) return `https://www.youtube.com/embed/${id}?rel=0`;
    }
    if (s.includes('vimeo.com/')) {
      const id = s.split('vimeo.com/')[1].split(/[?/]/)[0];
      if (id && /^\d+$/.test(id)) return `https://player.vimeo.com/video/${id}`;
    }
  } catch (e) {
    console.warn('getSafeVideoUrl: invalid URL', e.message);
  }
  return s;
};

// ── Map URL fixer ──────────────────────────────────────────────────────────────
const getSafeMapUrl = (url) => {
  if (!url) return '';
  if (url.includes('google.com/maps/d/viewer')) return url.replace('/viewer', '/embed');
  return url;
};

// ── Smooth-scroll anchor tabs ──────────────────────────────────────────────────
const SECTIONS = [
  { id: 'accueil',    label: 'Accueil' },
  { id: 'carte',      label: 'Carte' },
  { id: 'lieux',      label: 'Les lieux' },
  { id: 'classement', label: 'Classement' },
  { id: 'regles',     label: 'Règles' },
  { id: 'ressources', label: 'Ressources' },
  { id: 'galerie',    label: 'Galerie' },
  { id: 'actualites', label: 'Actualités' },
  { id: 'inscrire',   label: 'Inscrire son équipe' },
];

function SectionAnchor({ id }) {
  return <div id={id} style={{ scrollMarginTop: 128 }} />;
}

function StickyTabs({ territory }) {
  const [active, setActive] = useState('accueil');
  const color = territory?.color || 'var(--radar-deep)';

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) setActive(e.target.id); }),
      { rootMargin: '-30% 0px -60% 0px' },
    );
    SECTIONS.forEach(s => { const el = document.getElementById(s.id); if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, [territory]);

  const scrollTo = id => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <div className="sticky-tabs-bar" style={{ position: 'sticky', top: 'var(--nav-h, 64px)', zIndex: 90, background: 'rgba(244,241,236,.97)', backdropFilter: 'blur(8px)', borderBottom: '1px solid rgba(122,140,136,.15)', padding: '0 var(--px,clamp(16px,5vw,40px))', overflowX: 'auto', whiteSpace: 'nowrap' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', gap: 0 }}>
        {SECTIONS.map(s => (
          <button key={s.id} onClick={() => scrollTo(s.id)} style={{ padding: '13px 18px', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'Space Mono,monospace', fontSize: 10, letterSpacing: '.08em', textTransform: 'uppercase', color: active === s.id ? color : 'var(--radar-mid)', borderBottom: `2px solid ${active === s.id ? color : 'transparent'}`, transition: 'all .2s', flexShrink: 0 }}>
            {s.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Contact / Inscription ──────────────────────────────────────────────────────
function ContactOrganisateur({ territory }) {
  const [form,    setForm]    = useState({ nom: '', email: '', equipe: '', membres: '', formule: 'decouverte', message: '' });
  const [sent,    setSent]    = useState(false);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const submit = async () => {
    setLoading(true); setError('');
    try {
      const baseUrl      = process.env.REACT_APP_API_BASE || 'http://localhost:3001';
      const membersArray = form.membres ? form.membres.split(',').map(m => ({ nom: m.trim() })) : [];
      const response     = await fetch(`${baseUrl}/api/inscriptions/${territory.id}`, {
        method : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body   : JSON.stringify({ teamName: form.equipe, responsableNom: form.nom, responsableEmail: form.email, formula: form.formule, edition: new Date().getFullYear().toString(), members: membersArray, message: form.message }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP ${response.status}`);
      }
      setSent(true);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const IS = { width: '100%', padding: '11px 14px', border: '1px solid rgba(122,140,136,.3)', borderRadius: 4, fontFamily: 'Familjen Grotesk,sans-serif', fontSize: 14, outline: 'none', background: '#fff' };
  const LS = { display: 'block', fontFamily: 'Space Mono,monospace', fontSize: 10, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--radar-mid)', marginBottom: 6 };

  if (sent) return (
    <div style={{ textAlign: 'center', padding: '60px 0' }}>
      <div style={{ fontSize: 44, marginBottom: 16 }}>✅</div>
      <h3 style={{ fontSize: 22, fontWeight: 700, marginBottom: 10 }}>Inscription envoyée !</h3>
      <p style={{ color: '#5A6B68', marginBottom: 24 }}>L'organisateur a bien reçu votre demande et vous contactera prochainement.</p>
      <button onClick={() => { setSent(false); setForm({ nom: '', email: '', equipe: '', membres: '', formule: 'decouverte', message: '' }); }} style={{ padding: '10px 22px', background: 'none', border: '1.5px solid var(--radar-dark)', borderRadius: 4, fontFamily: 'Space Mono,monospace', fontSize: 11, cursor: 'pointer' }}>Nouvelle inscription</button>
    </div>
  );

  return (
    <div style={{ maxWidth: 560 }}>
      <p style={{ fontSize: 15, color: '#5A6B68', marginBottom: 28, lineHeight: 1.6 }}>Remplissez ce formulaire pour envoyer votre demande d'inscription directement à l'organisateur.</p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
        <div><label style={LS}>Votre nom *</label><input style={IS} value={form.nom}   onChange={e => setForm(f => ({ ...f, nom:   e.target.value }))} placeholder="Prénom Nom" /></div>
        <div><label style={LS}>Votre email *</label><input type="email" style={IS} value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="vous@exemple.fr" /></div>
      </div>
      <div style={{ marginBottom: 14 }}><label style={LS}>Nom de l'équipe *</label><input style={IS} value={form.equipe} onChange={e => setForm(f => ({ ...f, equipe: e.target.value }))} placeholder="Les Chamois Express..." /></div>
      <div style={{ marginBottom: 14 }}><label style={LS}>Membres (séparés par des virgules)</label><input style={IS} value={form.membres} onChange={e => setForm(f => ({ ...f, membres: e.target.value }))} placeholder="Léa M., Karim B., ..." /></div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
        <div><label style={LS}>Formule</label>
          <select style={IS} value={form.formule} onChange={e => setForm(f => ({ ...f, formule: e.target.value }))}>
            <option value="decouverte">Découverte</option>
            <option value="sport">Sport</option>
            <option value="expert">Expert</option>
          </select>
        </div>
      </div>
      <div style={{ marginBottom: 20 }}><label style={LS}>Message (optionnel)</label><textarea style={{ ...IS, resize: 'vertical' }} rows={3} value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} placeholder="Questions particulières..." /></div>
      {error && <p style={{ color: '#D94F00', fontSize: 13, marginBottom: 16 }}>{error}</p>}
      <button onClick={submit} disabled={!form.nom || !form.email || !form.equipe || loading}
        style={{ padding: '12px 28px', background: 'var(--radar-deep)', color: '#fff', border: 'none', borderRadius: 4, fontFamily: 'Space Mono,monospace', fontSize: 12, cursor: 'pointer', letterSpacing: '.05em', opacity: (!form.nom || !form.email || !form.equipe || loading) ? .5 : 1 }}>
        {loading ? 'Envoi en cours...' : "Envoyer l'inscription →"}
      </button>
    </div>
  );
}

// ── Rules accordion ────────────────────────────────────────────────────────────
function ReglesSection({ territory }) {
  const [open, setOpen] = useState(0);
  const rules = (territory.rules || []).sort((a, b) => a.order - b.order);
  return (
    <div style={{ maxWidth: 720 }}>
      {rules.length === 0
        ? <p style={{ color: 'var(--radar-mid)', fontSize: 14 }}>Règlement à venir.</p>
        : rules.map((r, i) => (
          <div key={i} style={{ borderBottom: '1px solid rgba(122,140,136,.15)' }}>
            <button onClick={() => setOpen(open === i ? null : i)} style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 0', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
              <span style={{ fontSize: 16, fontWeight: 600 }}>{r.title}</span>
              <span style={{ fontSize: 20, color: 'var(--radar-mid)', transform: open === i ? 'rotate(45deg)' : 'none', transition: 'transform .22s', flexShrink: 0 }}>+</span>
            </button>
            {open === i && <div style={{ padding: '0 0 18px', color: '#5A6B68', lineHeight: 1.7, fontSize: 14, whiteSpace: 'pre-wrap' }}>{r.content}</div>}
          </div>
        ))
      }
    </div>
  );
}

// ── Gallery ────────────────────────────────────────────────────────────────────
function GalerieSection({ territory }) {
  const [lightbox, setLightbox] = useState(null);
  const gallery = territory.gallery || [];
  const videos  = gallery.filter(g => g.type === 'video');
  const photos  = gallery.filter(g => g.type !== 'video');

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <p style={{ color: 'var(--radar-mid)', fontSize: 14 }}>{gallery.length} éléments</p>
      </div>

      {territory.instagramIframeSrc && (
        <div style={{ marginBottom: 28, borderRadius: 8, overflow: 'hidden', border: '1px solid rgba(122,140,136,.2)' }}>
          <iframe src={territory.instagramIframeSrc} width="100%" height="400" frameBorder="0" title="Instagram" style={{ display: 'block' }} />
        </div>
      )}

      {videos.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 12, marginBottom: 20 }}>
          {videos.map((v, i) => (
            <div key={i} style={{ borderRadius: 8, overflow: 'hidden', aspectRatio: '16/9', background: '#0F1E1B' }}>
              <iframe
                src={getSafeVideoUrl(v.url)}
                width="100%" height="100%"
                frameBorder="0" allowFullScreen
                title={`Video ${i}`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              />
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: 10 }}>
        {photos.map((img, i) => (
          <div key={i} onClick={() => setLightbox(img)} style={{ cursor: 'pointer', borderRadius: 6, overflow: 'hidden', aspectRatio: '4/3', background: '#E8EFE4' }}>
            <img
              src={resolveUrl(img.url, 'gallery')} alt={img.alt || ''}
              style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform .3s' }}
              onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.04)'}
              onMouseLeave={e => e.currentTarget.style.transform = ''}
            />
          </div>
        ))}
      </div>

      {lightbox && (
        <div onClick={() => setLightbox(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,30,27,.93)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40, cursor: 'pointer' }}>
          <img src={resolveUrl(lightbox.url, 'gallery')} alt="" style={{ maxWidth: '100%', maxHeight: '90vh', borderRadius: 6 }} />
        </div>
      )}
    </div>
  );
}

// ── Territory news ─────────────────────────────────────────────────────────────
function TerritoireNewsSection({ territory }) {
  const [articles, setArticles] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const navigate = useNavigate(); // ✅ correct : dans un composant

  useEffect(() => {
    import('../services/api').then(({ newsApi }) => {
      newsApi.list({ territory: territory.id, limit: 12 })
        .then(data => setArticles(data))
        .catch(console.error)
        .finally(() => setLoading(false));
    });
  }, [territory.id]);

  if (loading) return <p style={{ color: 'var(--radar-mid)', fontSize: 14 }}>Chargement...</p>;
  if (!articles.length) return (
    <div style={{ textAlign: 'center', padding: '48px 20px', background: '#fff', borderRadius: 10, border: '1px dashed rgba(255,107,0,.18)' }}>
      <div style={{ fontSize: 36, marginBottom: 12 }}>📰</div>
      <p style={{ color: 'var(--radar-mid)', fontSize: 14 }}>Aucun article pour ce territoire.</p>
    </div>
  );

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 18 }}>
      {articles.map(a => (
        <article key={a._id} onClick={() => navigate(`/actualites/${a._id}`)}
          style={{ background: '#fff', border: '1px solid rgba(255,107,0,.12)', borderRadius: 10, overflow: 'hidden', cursor: 'pointer', transition: 'all .2s' }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(255,107,0,.1)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}>
          {a.coverImage && (
            <div style={{ height: 160, overflow: 'hidden', background: '#1A0A00' }}>
              <img src={resolveUrl(a.coverImage, 'news')} alt={a.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
          )}
          <div style={{ padding: '16px 18px' }}>
            {a.isPinned && <span style={{ display: 'inline-block', background: '#FFF3E0', color: 'var(--radar-deep)', fontSize: 9, fontFamily: 'Space Mono,monospace', padding: '2px 7px', borderRadius: 2, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.08em' }}>📌 Épinglé</span>}
            <h3 style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.3, marginBottom: 6, color: 'var(--radar-dark)' }}>{a.title}</h3>
            {a.excerpt && <p style={{ fontSize: 12, color: '#5A6B68', lineHeight: 1.55, marginBottom: 10 }}>{a.excerpt.substring(0, 90)}{a.excerpt.length > 90 ? '…' : ''}</p>}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <time style={{ fontSize: 10, color: 'var(--radar-mid)', fontFamily: 'Space Mono,monospace' }}>
                {new Date(a.createdAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
              </time>
              <span style={{ fontSize: 11, color: 'var(--radar-deep)', fontFamily: 'Space Mono,monospace', fontWeight: 600 }}>Lire →</span>
            </div>
          </div>
        </article>
      ))}
    </div>
  );
}

// ── Places / Gogocarto ─────────────────────────────────────────────────────────
function LieuxSection({ territory }) {
  const places   = (territory.places || []).sort((a, b) => (a.order || 0) - (b.order || 0));
  const [expanded, setExpanded] = React.useState(null);

  return (
    <div>
      {territory.gogocartoUrl && (
        <div style={{ marginBottom: 32, borderRadius: 10, overflow: 'hidden', border: '1px solid rgba(255,107,0,.12)', boxShadow: '0 2px 12px rgba(0,0,0,.06)' }}>
          
        </div>
      )}
      {places.length > 0 && (
        <div>
          <p style={{ fontFamily: 'Space Mono,monospace', fontSize: 10, color: 'var(--radar-mid)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 16 }}>
            {places.length} lieu{places.length > 1 ? 'x' : ''} à explorer
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 14 }}>
            {places.map((p, i) => {
              const displayDetails = p.details || p.description || p.detail || p.info || null;
              return (
                <div key={i} onClick={() => setExpanded(expanded === i ? null : i)}
                  style={{ background: '#fff', border: `1.5px solid ${expanded === i ? 'var(--radar-deep)' : 'rgba(255,107,0,.12)'}`, borderRadius: 10, padding: '18px 20px', cursor: 'pointer', transition: 'all .2s', boxShadow: expanded === i ? '0 6px 24px rgba(255,107,0,.14)' : '0 1px 4px rgba(0,0,0,.04)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--radar-dark)', marginBottom: 3 }}>{p.name}</div>
                      {p.address && <div style={{ fontSize: 12, color: 'var(--radar-mid)', display: 'flex', alignItems: 'center', gap: 4 }}>📍 {p.address}</div>}
                    </div>
                    <span style={{ fontSize: 11, color: 'var(--radar-deep)', fontWeight: 700, marginLeft: 8, flexShrink: 0, opacity: .7 }}>{expanded === i ? '▲' : '▼'}</span>
                  </div>
                  {expanded === i && (
                    <div style={{ borderTop: '1px solid rgba(255,107,0,.1)', paddingTop: 12, marginTop: 12, display: 'flex', flexDirection: 'column', gap: 9 }}>
                      {displayDetails && <p style={{ fontSize: 13, color: '#444', fontStyle: 'italic', marginBottom: 8, whiteSpace: 'pre-wrap' }}>{displayDetails}</p>}
                      {p.contact && <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><span>👤</span><span style={{ fontSize: 13, fontWeight: 500 }}>{p.contact}</span></div>}
                      {p.phone   && <a href={`tel:${p.phone}`}     onClick={e => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'inherit' }}><span>📞</span><span style={{ fontSize: 13, color: 'var(--radar-deep)', fontWeight: 500 }}>{p.phone}</span></a>}
                      {p.email   && <a href={`mailto:${p.email}`}  onClick={e => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'inherit' }}><span>✉️</span><span style={{ fontSize: 13, color: 'var(--radar-deep)', wordBreak: 'break-all' }}>{p.email}</span></a>}
                      {!p.contact && !p.phone && !p.email && !displayDetails && <p style={{ fontSize: 12, color: 'var(--radar-mid)', fontStyle: 'italic' }}>Pas d'informations supplémentaires.</p>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Territory-specific resources ───────────────────────────────────────────────
function RessourcesSection({ territorySlug }) {
  const [resources, setResources] = useState([]);
  useEffect(() => {
    resourcesApi.list({ territory: territorySlug }).then(setResources).catch(console.error);
  }, [territorySlug]);
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(240px,1fr))', gap: 16 }}>
      {resources.length === 0 && <p style={{ color: 'var(--radar-mid)', fontSize: 14 }}>Aucune ressource disponible.</p>}
      {resources.map(r => (
        <div key={r._id} style={{ background: '#fff', border: '1px solid rgba(122,140,136,.18)', borderRadius: 6, padding: '20px 20px 16px' }}>
          <div style={{ fontSize: 26, marginBottom: 10 }}>{r.icon || '📄'}</div>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6 }}>{r.title}</div>
          <div style={{ fontSize: 12, color: 'var(--radar-mid)', marginBottom: 14 }}>{r.description?.substring(0, 60)}{r.description?.length > 60 ? '…' : ''}</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 10, fontFamily: 'Space Mono,monospace', padding: '2px 7px', background: 'var(--radar-accent3, #FFF3E0)', color: 'var(--radar-deep)', borderRadius: 2 }}>{r.format}</span>
            <button onClick={() => window.open(r.fileUrl || r.externalUrl, '_blank')} disabled={!r.fileUrl && !r.externalUrl}
              style={{ padding: '5px 12px', background: 'var(--radar-deep)', color: '#fff', border: 'none', borderRadius: 3, fontFamily: 'Space Mono,monospace', fontSize: 10, cursor: 'pointer', opacity: (!r.fileUrl && !r.externalUrl) ? .4 : 1 }}>
              Télécharger
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Territory partners strip ───────────────────────────────────────────────────
function TerritoryPartnersStrip({ partners }) {
  if (!partners || !partners.length) return null;
  return (
    <div style={{ marginTop: 32, paddingTop: 24, borderTop: '1px solid rgba(255,255,255,.15)' }}>
      <p style={{ fontFamily: 'Space Mono,monospace', fontSize: 10, color: 'rgba(255,255,255,.6)', letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 16 }}>Partenaires</p>
      <div style={{ display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
        {partners.map(p => (
          <a key={p._id} href={p.website || '#'} target="_blank" rel="noopener noreferrer"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: .8, transition: 'opacity .2s' }}
            onMouseEnter={e => e.currentTarget.style.opacity = '1'}
            onMouseLeave={e => e.currentTarget.style.opacity = '.8'}>
            {p.logoUrl
              ? <img src={resolveUrl(p.logoUrl, 'logos')} alt={p.name} style={{ maxHeight: 44, maxWidth: 120, objectFit: 'contain' }} />
              : <div style={{ padding: '6px 14px', background: 'rgba(255,255,255,.1)', borderRadius: 4, fontFamily: 'Space Mono,monospace', fontSize: 11, color: '#fff', fontWeight: 700 }}>{p.name}</div>
            }
          </a>
        ))}
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────
export default function TerritoirePage() {
  // ✅ Tous les hooks en premier, aucune condition avant eux
  const [searchParams] = useSearchParams();
  const { slug }       = useParams();    // ✅ dans le composant
  const navigate       = useNavigate();  // ✅ dans le composant

  const [territories,   setTerritories]   = useState([]);
  const [selected,      setSelected]      = useState(null);
  const [fullTerritory, setFullTerritory] = useState(null);
  const [partners,      setPartners]      = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [heroVis,       setHeroVis]       = useState(false);
  const [offsetY,       setOffsetY]       = useState(0);

  useEffect(() => {
    setHeroVis(true);
    const handleScroll = () => setOffsetY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const preselect = slug || searchParams.get('t');
    territoriesApi.list().then(data => {
      setTerritories(data);
      const target = preselect
        ? data.find(t => t.id === preselect || t.slug === preselect)
        : null;
      setSelected(target || data[0] || null);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [slug]); // ← slug seulement, pas searchParams

  useEffect(() => {
    if (!selected) return;
    territoriesApi.get(selected.id).then(t => {
      setFullTerritory(t);
    }).catch(console.error);
    partnersApi.listByTerritory(selected.id).then(setPartners).catch(console.error);
  }, [selected]);

  const t = fullTerritory;

  if (loading) return <div style={{ paddingTop: 120, textAlign: 'center', color: 'var(--radar-mid)' }}>Chargement...</div>;

  return (
    <div style={{ paddingTop: 'var(--nav-h, 64px)' }}>
      <style>{`
        .hero-parallax-img {
          position: absolute; top: 5%; right: 5%; width: 450px;
          pointer-events: none; z-index: 0; opacity: 0.8;
          transition: transform 0.1s ease-out;
        }
        .hero-text-container {
          max-width: 1100px; margin: 0 auto;
          position: relative; z-index: 1; width: 100%;
        }
        .territoire-hero-img {
          position: absolute; top: -15%; left: 0;
          width: 100%; height: 130%; object-fit: cover;
          z-index: 0; transition: transform 0.1s ease-out;
        }
        .territoire-hero-gradient {
          position: absolute; inset: 0; z-index: 1;
          background: linear-gradient(160deg, rgba(26,10,0,0.88) 0%, rgba(26,10,0,0.72) 60%, rgba(26,10,0,0.55) 100%);
        }
        .territoire-hero-container {
          position: relative; z-index: 2;
          max-width: 1100px; margin: 0 auto;
          padding: 56px 40px;
        }
        .territoire-global-hero {
          min-height: 80vh;
          display: flex;
          flex-direction: column;
          justify-content: center;
          padding: 80px 40px 40px;
          position: relative;
          overflow: hidden;
          background: linear-gradient(160deg, #F4F1EC 55%, #E8F4F1 100%);
        }
        @media (max-width: 768px) {
          .hero-parallax-img { width: 150%; right: -25%; top: 15%; opacity: 0.25; }
          .territoire-hero-container { padding: 32px 16px; }
          .territoire-hero-gradient {
            background: linear-gradient(160deg, rgba(26,10,0,0.93) 0%, rgba(26,10,0,0.87) 60%, rgba(26,10,0,0.75) 100%);
          }
          .sticky-tabs-bar { padding: 0 4px !important; }
          .territoire-global-hero {
            padding: 56px 16px 28px !important;
            min-height: 55vh !important;
          }
        }
      `}</style>

      {/* ── HERO GLOBAL ── */}
      <section className="territoire-global-hero">
        <img src={heroImage} alt="" className="hero-parallax-img" style={{ transform: `translateY(${offsetY * 0.4}px)` }} />
        <div className="hero-text-container">
          <div style={{ opacity: heroVis ? 1 : 0, transform: heroVis ? 'none' : 'translateX(-20px)', transition: 'all .8s ease .1s' }}>
            <p className="section-label" style={{ marginBottom: 18 }}>Jeu de piste</p>
          </div>
          <div style={{ opacity: heroVis ? 1 : 0, transform: heroVis ? 'none' : 'translateY(18px)', transition: 'all .9s ease .3s' }}>
            <h1 style={{ fontSize: 'clamp(36px,7.5vw,88px)', fontWeight: 700, lineHeight: 1.0, letterSpacing: '-.03em', marginBottom: 24, color: 'var(--radar-dark)' }}>
              Les Territoires
            </h1>
          </div>
          <div style={{ opacity: heroVis ? 1 : 0, transform: heroVis ? 'none' : 'translateY(16px)', transition: 'all .9s ease .5s' }}>
            <p style={{ fontSize: 'clamp(14px,2.5vw,18px)', color: '#3D5550', maxWidth: 500, lineHeight: 1.65, marginBottom: 40 }}>
              Sélectionnez un territoire pour parcourir toutes ses informations en une seule page.
            </p>
          </div>
        </div>
      </section>

      {/* ── TERRITORY GRID ── */}
      <section style={{ padding: 'clamp(40px,7vw,80px) var(--px,clamp(16px,5vw,40px))', background: '#fff' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 44, flexWrap: 'wrap', gap: 12 }}>
            <div>
              <p className="section-label" style={{ marginBottom: 10 }}>Les territoires actifs</p>
              <h2 style={{ fontSize: 'clamp(22px,4vw,34px)', fontWeight: 700, letterSpacing: '-.02em' }}>
                {territories.length || 3} territoire{territories.length !== 1 ? 's' : ''} vous attendent
              </h2>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(290px,1fr))', gap: 20 }}>
            {territories.map((territoireIter, i) => (
              <div key={territoireIter.id}
                onClick={() => {
                  navigate(`/territoire/${territoireIter.slug || territoireIter.id}`);
                  setTimeout(() => document.getElementById('accueil')?.scrollIntoView({ behavior: 'smooth' }), 150);
                }}
                style={{ cursor: 'pointer' }}>
                <TerritoryCard t={territoireIter} index={i} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {t && <StickyTabs territory={t} />}

      {/* ── TERRITORY DETAIL ── */}
      {t && (
        <div>
          {/* ── ACCUEIL ── */}
          <SectionAnchor id="accueil" />
          <section style={{ position: 'relative', minHeight: 360, overflow: 'hidden', background: '#1A0A00' }}>
            {t.heroImageUrl ? (
              <>
                <img src={resolveUrl(t.heroImageUrl, 'heroes')} alt={`Fond ${t.name}`} className="territoire-hero-img" style={{ transform: `translateY(${offsetY * 0.25}px)` }} />
                <div className="territoire-hero-gradient" />
              </>
            ) : (
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(160deg, #1A0A00 60%, #301705 100%)', zIndex: 0 }} />
            )}
            <div className="territoire-hero-container">
              <div style={{ display: 'flex', gap: 20, alignItems: 'center', marginBottom: 28, flexWrap: 'wrap' }}>
                {t.avatarUrl && (
                  <div style={{ width: 72, height: 72, borderRadius: '50%', overflow: 'hidden', border: `3px solid ${t.color}`, flexShrink: 0, boxShadow: '0 4px 16px rgba(0,0,0,.25)' }}>
                    <img src={resolveUrl(t.avatarUrl, 'avatars')} alt={t.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                )}
                <div>
                  <h2 style={{ fontSize: 'clamp(22px,5vw,32px)', fontWeight: 700, letterSpacing: '-.02em', marginBottom: 4, color: '#fff' }}>RADAR {t.name}</h2>
                  <p style={{ fontFamily: 'Space Mono,monospace', fontSize: 11, color: 'rgba(255,255,255,.7)' }}>
                    {t.region}{t.department ? ` · ${t.department}` : ''} · {t.players || 0} participants · {t.teams || 0} équipes
                  </p>
                </div>
                {t.instagramHandle && (
                  <a href={`https://instagram.com/${t.instagramHandle}`} target="_blank" rel="noopener noreferrer"
                    style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: 'rgba(255,255,255,.15)', backdropFilter: 'blur(4px)', border: '1px solid rgba(255,255,255,.25)', borderRadius: 6, fontFamily: 'Space Mono,monospace', fontSize: 11, color: '#fff', textDecoration: 'none', whiteSpace: 'nowrap' }}>
                    📸 @{t.instagramHandle}
                  </a>
                )}
              </div>

              {t.description && (
                <p style={{ fontSize: 16, color: 'rgba(255,255,255,.9)', lineHeight: 1.7, maxWidth: 680, marginBottom: 24 }}>{t.description}</p>
              )}

              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
                {[
                  t.nextEvent && { label: 'Prochain événement', val: new Date(t.nextEvent).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }), color: t.color },
                  (t.periodStart || t.periodEnd) && { label: 'Période de jeu', val: `${t.periodStart ? new Date(t.periodStart).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) : '…'} → ${t.periodEnd ? new Date(t.periodEnd).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) : '…'}` },
                ].filter(Boolean).map((s, i) => (
                  <div key={i} style={{ padding: '14px 20px', background: 'rgba(255,255,255,.12)', backdropFilter: 'blur(6px)', border: '1px solid rgba(255,255,255,.2)', borderRadius: 8 }}>
                    <div style={{ fontSize: 10, fontFamily: 'Space Mono,monospace', letterSpacing: '.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,.7)', marginBottom: 4 }}>{s.label}</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: s.color || '#fff' }}>{s.val}</div>
                  </div>
                ))}
              </div>

              {(t.contact || t.phone) && (
                <div style={{ padding: '16px 20px', background: 'rgba(255,255,255,.08)', backdropFilter: 'blur(6px)', border: '1px solid rgba(255,255,255,.15)', borderRadius: 8, marginBottom: 24, maxWidth: 640 }}>
                  <div style={{ fontSize: 10, fontFamily: 'Space Mono,monospace', letterSpacing: '.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,.7)', marginBottom: 12 }}>Contacter l'organisateur</div>
                  <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                    {t.contact && <a href={`mailto:${t.contact}`} style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#fff', textDecoration: 'none', fontSize: 14, fontWeight: 500 }} onMouseEnter={e => e.currentTarget.style.opacity = .8} onMouseLeave={e => e.currentTarget.style.opacity = 1}>✉️ {t.contact}</a>}
                    {t.phone   && <a href={`tel:${t.phone}`}     style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#fff', textDecoration: 'none', fontSize: 14, fontWeight: 500 }} onMouseEnter={e => e.currentTarget.style.opacity = .8} onMouseLeave={e => e.currentTarget.style.opacity = 1}>📞 {t.phone}</a>}
                  </div>
                </div>
              )}

              {t.videoUrl && (
                <div style={{ maxWidth: 640, borderRadius: 10, overflow: 'hidden', aspectRatio: '16/9', marginBottom: 24, boxShadow: '0 8px 32px rgba(0,0,0,.3)' }}>
                  <iframe
                    src={getSafeVideoUrl(t.videoUrl)}
                    width="100%" height="100%"
                    frameBorder="0" allowFullScreen
                    title={`Vidéo RADAR ${t.name}`}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  />
                </div>
              )}

              <TerritoryPartnersStrip partners={partners} />
            </div>
          </section>

          {/* ── CARTE ── */}
          <SectionAnchor id="carte" />
          <section style={{ padding: 'clamp(32px,6vw,64px) var(--px,clamp(16px,5vw,40px))', background: 'var(--radar-cream)' }}>
            <div style={{ maxWidth: 1100, margin: '0 auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
                <h2 style={{ fontSize: 26, fontWeight: 700 }}>Carte du parcours</h2>
                {t.mapUrl && (
                  <a href={t.mapUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '9px 18px', background: 'var(--radar-deep)', color: '#fff', borderRadius: 6, fontFamily: 'Space Mono,monospace', fontSize: 11, letterSpacing: '.04em' }}>
                    🔗 Ouvrir en plein écran
                  </a>
                )}
              </div>
              {t.mapUrl ? (
                <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid rgba(255,107,0,.15)', boxShadow: '0 4px 20px rgba(0,0,0,.08)', aspectRatio: '16/9', minHeight: 400, background: '#E8EFE4' }}>
                  <iframe src={getSafeMapUrl(t.mapUrl)} width="100%" height="100%" frameBorder="0" style={{ display: 'block', width: '100%', height: '100%', minHeight: 400 }} title={`Carte RADAR ${t.name}`} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
                </div>
              ) : (
                <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid rgba(122,140,136,.2)' }}>
                  <InteractiveMap territory={t} />
                </div>
              )}
            </div>
          </section>

          {/* ── LIEUX ── */}
          <SectionAnchor id="lieux" />
          <section style={{ padding: 'clamp(32px,6vw,64px) var(--px,clamp(16px,5vw,40px))', background: '#fff' }}>
            <div style={{ maxWidth: 1100, margin: '0 auto' }}>
              
              <LieuxSection territory={t} />
            </div>
          </section>

          

          {/* ── CLASSEMENT ── */}
          <SectionAnchor id="classement" />
          <section style={{ padding: 'clamp(32px,6vw,64px) var(--px,clamp(16px,5vw,40px))', background: '#fff' }}>
            <div style={{ maxWidth: 1100, margin: '0 auto' }}>
              <h2 style={{ fontSize: 26, fontWeight: 700, marginBottom: 24 }}>Classement</h2>
              <LiveRanking territory={t} />
            </div>
          </section>

          {/* ── RÈGLES ── */}
          <SectionAnchor id="regles" />
          <section style={{ padding: 'clamp(32px,6vw,64px) var(--px,clamp(16px,5vw,40px))', background: '#fff' }}>
            <div style={{ maxWidth: 1100, margin: '0 auto' }}>
              <h2 style={{ fontSize: 26, fontWeight: 700, marginBottom: 8 }}>Règles</h2>
              <p style={{ color: 'var(--radar-mid)', fontSize: 13, marginBottom: 28 }}>Règlement spécifique à RADAR {t.name}</p>
              <ReglesSection territory={t} />
            </div>
          </section>

          {/* ── RESSOURCES ── */}
          <SectionAnchor id="ressources" />
          <section style={{ padding: 'clamp(32px,6vw,64px) var(--px,clamp(16px,5vw,40px))', background: 'var(--radar-cream)' }}>
            <div style={{ maxWidth: 1100, margin: '0 auto' }}>
              <h2 style={{ fontSize: 26, fontWeight: 700, marginBottom: 24 }}>Ressources</h2>
              <RessourcesSection territorySlug={t.id} />
            </div>
          </section>

          {/* ── GALERIE ── */}
          <SectionAnchor id="galerie" />
          <section style={{ padding: 'clamp(32px,6vw,64px) var(--px,clamp(16px,5vw,40px))', background: 'var(--radar-cream)' }}>
            <div style={{ maxWidth: 1100, margin: '0 auto' }}>
              <h2 style={{ fontSize: 26, fontWeight: 700, marginBottom: 24 }}>Galerie</h2>
              <GalerieSection territory={t} />
            </div>
          </section>

          {/* ── ACTUALITÉS ── */}
          <SectionAnchor id="actualites" />
          <section style={{ padding: 'clamp(32px,6vw,64px) var(--px,clamp(16px,5vw,40px))', background: '#fff' }}>
            <div style={{ maxWidth: 1100, margin: '0 auto' }}>
              <h2 style={{ fontSize: 26, fontWeight: 700, marginBottom: 8 }}>Actualités</h2>
              <p style={{ color: 'var(--radar-mid)', fontSize: 13, marginBottom: 28 }}>Articles et nouvelles de RADAR {t.name}</p>
              <TerritoireNewsSection territory={t} />
            </div>
          </section>

          {/* ── INSCRIPTION ── */}
          <SectionAnchor id="inscrire" />
          <section style={{ padding: 'clamp(32px,6vw,64px) var(--px,clamp(16px,5vw,40px))', background: 'var(--radar-cream)' }}>
            <div style={{ maxWidth: 1100, margin: '0 auto' }}>
              <h2 style={{ fontSize: 26, fontWeight: 700, marginBottom: 8 }}>Inscrire son équipe</h2>
              <p style={{ color: 'var(--radar-mid)', fontFamily: 'Space Mono,monospace', fontSize: 11, marginBottom: 28 }}>Envoyer directement au responsable</p>
              <ContactOrganisateur territory={t} />
            </div>
          </section>
        </div>
      )}
    </div>
  );
}